import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useId, useState } from 'react';
import { Button } from '../../primitives/Button.js';
import { Input } from '../../primitives/Input.js';
import { Stack } from '../../primitives/Stack.js';
import { describedBy, FieldHost } from '../FieldHost.js';
import { useLanguage } from '../../i18n/LanguageProvider.js';
function asAttachment(v) {
    return v !== null && typeof v === 'object' ? v : undefined;
}
function humanSize(bytes) {
    if (bytes === undefined || !Number.isFinite(bytes))
        return '';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/**
 * File upload field with mobile-friendly affordances:
 *   • Drag-or-tap drop zone (the native `<input type=file>` stays the
 *     interaction surface so iOS Safari's "Take Photo / Photo Library /
 *     Choose File" sheet still works).
 *   • Thumbnail preview for image MIME types (read once into a data URL).
 *   • Visible per-file upload progress + status badge, driven off the
 *     `_status` / `_progress` carriers on the attachment value.
 *   • Retry button when `_status === 'error'`. Retry just re-fires
 *     `onChange` with the same file, which signals the host to start a
 *     fresh upload.
 *   • Remove button to clear the field.
 */
export function FileUpload(props) {
    const { item, value, errors, disabled, onChange } = props;
    const { t } = useLanguage();
    const id = useId();
    const current = asAttachment(value);
    const [thumbnail, setThumbnail] = useState(undefined);
    // Build a data-URL thumbnail for image attachments so the patient
    // sees what they uploaded, not just a filename. We only attempt this
    // for image/* content types because non-image files (PDFs) don't yield
    // anything useful from `FileReader.readAsDataURL`.
    useEffect(() => {
        const file = current?._file;
        if (file === undefined || !file.type.startsWith('image/')) {
            setThumbnail(undefined);
            return;
        }
        const reader = new FileReader();
        let cancelled = false;
        reader.onload = () => {
            if (cancelled)
                return;
            const result = reader.result;
            if (typeof result === 'string')
                setThumbnail(result);
        };
        reader.readAsDataURL(file);
        return () => {
            cancelled = true;
        };
    }, [current?._file]);
    const status = current?._status ?? (current?._file !== undefined ? 'idle' : undefined);
    const progress = Math.max(0, Math.min(100, current?._progress ?? 0));
    const pickFile = (file) => {
        if (file === undefined) {
            onChange(undefined);
            return;
        }
        const attachment = {
            contentType: file.type === '' ? 'application/octet-stream' : file.type,
            size: file.size,
            title: file.name,
            _file: file,
            _status: 'uploading',
            _progress: 0,
        };
        onChange(attachment);
    };
    const retry = () => {
        if (current === undefined)
            return;
        // Re-emit the attachment with status reset so the host picks it up
        // as a fresh upload. The `_file` carrier is the source of truth.
        onChange({
            ...current,
            _status: 'uploading',
            _progress: 0,
            _error: undefined,
        });
    };
    const remove = () => {
        onChange(undefined);
    };
    return (_jsx(FieldHost, { item: item, errors: errors, controlId: id, children: (ctx) => (_jsxs(Stack, { gap: 8, "data-file-upload": "true", children: [_jsx(Input, { id: id, type: "file", disabled: disabled === true, required: item.required === true && current === undefined, "aria-invalid": ctx.hasError || undefined, "aria-describedby": describedBy(ctx), onChange: (e) => {
                        pickFile(e.currentTarget.files?.[0]);
                    } }), current !== undefined ? (_jsxs("div", { className: "file-preview", "data-file-status": status ?? 'idle', children: [_jsx("div", { className: "file-preview-thumb", "aria-hidden": "true", children: thumbnail !== undefined ? (_jsx("img", { src: thumbnail, alt: "" })) : (_jsx("span", { className: "file-preview-glyph", children: "\uD83D\uDCCE" })) }), _jsxs("div", { className: "file-preview-body", children: [_jsx("div", { className: "file-preview-name", "data-file-name": "true", children: current.title ?? 'file' }), _jsxs("div", { className: "file-preview-meta muted", children: [humanSize(current.size), status !== undefined ? _jsxs(_Fragment, { children: [" \u00B7 ", t(statusLabelKey(status))] }) : null] }), status === 'uploading' ? (_jsx("div", { className: "file-progress", role: "progressbar", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": progress, "aria-label": "Upload progress", children: _jsx("div", { className: "file-progress-bar", style: { width: `${progress}%` } }) })) : null, status === 'error' && current._error !== undefined ? (_jsx("div", { className: "file-error", children: current._error })) : null, status === 'uploaded' && current._ocrLowConfidence === true ? (_jsx("div", { className: "file-ocr-warning", role: "status", "data-ocr-low-confidence": "true", children: t('fields.attachment.ocr-low-confidence') })) : null] }), _jsxs("div", { className: "file-preview-actions", children: [status === 'error' ? (_jsx(Button, { type: "button", variant: "secondary", onClick: retry, disabled: disabled === true, children: "Retry" })) : null, _jsx(Button, { type: "button", variant: "ghost", onClick: remove, disabled: disabled === true, "aria-label": "Remove file", children: "\u00D7" })] })] })) : null] })) }));
}
function statusLabelKey(status) {
    switch (status) {
        case 'uploading':
            return 'fields.attachment.uploading';
        case 'uploaded':
            return 'fields.attachment.uploaded';
        case 'error':
            return 'fields.attachment.failed';
        case 'idle':
            return 'fields.attachment.ready';
    }
}
//# sourceMappingURL=FileUpload.js.map